/**
 * Conflict Resolution Modal Component
 * Shows detailed info about a sync conflict and resolution options
 */

export default function ConflictResolutionModal({
  conflict,
  onResolve,
  onClose
}) {
  if (!conflict) return null;

  const { transaction, conflictType, serverResponse, detectedAt } = conflict;

  const getConflictTitle = () => {
    switch (conflictType) {
      case 'ALREADY_CHECKED_OUT':
        return 'โต๊ะถูกคิดเงินไปแล้ว';
      case 'SESSION_MISMATCH':
        return 'โต๊ะเปิดใหม่กับลูกค้ากลุ่มใหม่';
      case 'TABLE_ALREADY_CLOSED':
        return 'โต๊ะถูกปิดไปแล้ว';
      default:
        return 'เกิดข้อขัดแย้ง';
    }
  };

  const getConflictDescription = () => {
    switch (conflictType) {
      case 'ALREADY_CHECKED_OUT':
        return 'โต๊ะนี้ถูกคิดเงินโดยผู้ดูแลคนอื่นหรือจากอุปกรณ์อื่นแล้ว ไม่จำเป็นต้องซิงค์รายการนี้ซ้ำ';
      case 'SESSION_MISMATCH':
        return 'โต๊ะนี้ถูกเปิดใหม่กับลูกค้ากลุ่มใหม่แล้ว กรุณาตรวจสอบว่ารายการนี้ควรถูกยกเลิกหรือดำเนินการอื่น';
      case 'TABLE_ALREADY_CLOSED':
        return 'โต๊ะนี้ถูกปิดไปแล้ว อาจถูกคิดเงินโดยไม่ผ่านระบบออฟไลน์';
      default:
        return 'เกิดข้อขัดแย้งในการซิงค์ข้อมูล กรุณาตรวจสอบและเลือกการดำเนินการ';
    }
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleString('th-TH', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000,
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        // body ตั้งสีตัวอักษรเริ่มต้นเป็น #e0e0e0 (ธีมมืด) — กล่องพื้นสว่างต้องกำหนดสีเอง
        color: '#2d3748',
        borderRadius: '16px',
        maxWidth: '500px',
        width: '100%',
        maxHeight: '80vh',
        overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          color: 'white',
          padding: '24px',
          borderRadius: '16px 16px 0 0'
        }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>⚠️</div>
          <h2 style={{ margin: '0 0 8px 0', fontSize: '22px' }}>
            {getConflictTitle()}
          </h2>
          <p style={{ margin: 0, fontSize: '14px', opacity: 0.9 }}>
            โต๊ะ {transaction?.tableCode}
          </p>
        </div>

        {/* Content */}
        <div style={{ padding: '24px' }}>
          {/* Description */}
          <div style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            padding: '14px',
            borderRadius: '10px',
            marginBottom: '20px',
            fontSize: '14px',
            color: '#991b1b',
            lineHeight: 1.6
          }}>
            {getConflictDescription()}
          </div>

          {/* Transaction Details */}
          <div style={{
            background: '#f9fafb',
            padding: '16px',
            borderRadius: '10px',
            marginBottom: '20px'
          }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '15px', color: '#374151' }}>
              📋 รายละเอียดรายการ
            </h4>
            <div style={{
              display: 'grid',
              gap: '8px',
              fontSize: '14px',
              color: '#4b5563'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>โต๊ะ:</span>
                <strong>{transaction?.tableCode}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>ยอดรวม:</span>
                <strong>{(transaction?.grandTotal || 0).toFixed(2)} ฿</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>สร้างเมื่อ:</span>
                <span>{formatDateTime(transaction?.createdAt)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>ตรวจพบปัญหา:</span>
                <span>{formatDateTime(detectedAt)}</span>
              </div>
            </div>
          </div>

          {/* Server Response (if any) */}
          {serverResponse?.data?.existingTransaction && (
            <div style={{
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              padding: '16px',
              borderRadius: '10px',
              marginBottom: '20px'
            }}>
              <h4 style={{ margin: '0 0 12px 0', fontSize: '15px', color: '#166534' }}>
                ✅ รายการที่ซิงค์แล้ว
              </h4>
              <div style={{
                fontSize: '14px',
                color: '#166534'
              }}>
                <div>เลขที่ใบเสร็จ: {serverResponse.data.existingTransaction.receiptId}</div>
                <div>ยอด: {serverResponse.data.existingTransaction.grandTotal?.toFixed(2)} ฿</div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: '12px' }}>
            {conflictType === 'ALREADY_CHECKED_OUT' || conflictType === 'TABLE_ALREADY_CLOSED' ? (
              // Auto-resolvable conflicts
              <button
                onClick={() => onResolve(conflict.offlineId, 'skip', 'ข้ามเนื่องจากมีรายการซ้ำ')}
                style={{
                  flex: 1,
                  padding: '14px',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                ✅ ข้ามรายการนี้
              </button>
            ) : (
              // Manual resolution needed
              <>
                <button
                  onClick={() => onResolve(conflict.offlineId, 'skip', 'ข้ามโดยผู้ดูแล')}
                  style={{
                    flex: 1,
                    padding: '14px',
                    background: '#f59e0b',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  ⏭️ ข้าม
                </button>
                <button
                  onClick={() => onResolve(conflict.offlineId, 'retry', null)}
                  style={{
                    flex: 1,
                    padding: '14px',
                    background: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  🔄 ลองใหม่
                </button>
              </>
            )}
          </div>

          {/* Close button */}
          <button
            onClick={onClose}
            style={{
              width: '100%',
              marginTop: '12px',
              padding: '12px',
              background: '#e5e7eb',
              color: '#4b5563',
              border: 'none',
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            ปิด
          </button>
        </div>
      </div>
    </div>
  );
}
